// EBITDABridgePercent.tsx
import React from 'react';
import { AccountEntry, CategoryOption, Project } from '../../types';
import {
  calculateTTM,
  calculateTTMForPeriodType,
  calculateCategoryTotal,
  calculateEBITDA,
  compareMonthYear
} from '../../lib/financial/calculations';

interface EBITDABridgePercentProps {
  accounts: AccountEntry[];
  periods: string[];
  viewType: 'monthly' | 'quarterly' | 'yearly';
  allMonths: string[];
  calculateNetIncome: (period: string) => number;
  calculateNetRevenueTotal: (period: string) => number;
  ttmNetIncome?: number;
  templateCategories?: CategoryOption[];
  selectedProject?: Project | null;
}

export const EBITDABridgePercent: React.FC<EBITDABridgePercentProps> = ({
  accounts,
  periods,
  viewType,
  allMonths,
  calculateNetIncome,
  calculateNetRevenueTotal,
  ttmNetIncome,
  templateCategories,
  selectedProject
}) => {
  const ebitdaAddBacks = [
    { category: 'Interest Expense (Income)', label: 'Add: Interest Expense (Income)' },
    { category: 'Income Tax Expense', label: 'Add: Income Tax Expense' },
    { category: 'Depreciation', label: 'Add: Depreciation' },
    { category: 'Amortization', label: 'Add: Amortization' }
  ];

  // Helper function to check if EBITDA categories are available in the template
  const hasEBITDACategory = (category: string) => {
    if (!templateCategories) return true; // Default to true if no template
    
    // Check if category exists in template by value or label
    return templateCategories.some(tc => 
      tc.value === category || tc.label === category
    );
  };

  // Get period-aware lookback periods for TTM calculations
  const getTTMPeriods = () => {
    const projectPeriodType = selectedProject?.period_type || 'monthly';
    const sortedMonths = [...allMonths].sort(compareMonthYear);
    
    let lookbackPeriods: number;
    switch (projectPeriodType) {
      case 'quarterly':
        lookbackPeriods = 4; // Last 4 quarters (1 year)
        break;
      case 'annual':
        lookbackPeriods = 1; // Last 1 year
        break;
      default: // monthly
        lookbackPeriods = 12; // Last 12 months (1 year)
        break;
    }
    
    return sortedMonths.slice(Math.max(0, sortedMonths.length - lookbackPeriods));
  };

  // If none of the EBITDA categories exist in the template, don't render the bridge
  const anyEBITDACategoriesExist = ebitdaAddBacks.some(item => hasEBITDACategory(item.category));
  
  if (!anyEBITDACategoriesExist) {
    return null;
  }

  return (
    <>
      {/* EBITDA Bridge Section Header */}
      <tr className="bg-gray-50">
        <td className="sticky left-0 z-10 bg-gray-50 px-6 py-3 text-sm font-semibold text-gray-900 border-r border-gray-200">
          EBITDA Bridge
        </td>
        <td className="px-6 py-4 text-sm font-semibold" />
        <td colSpan={periods.length + (viewType === 'yearly' ? 1 : 0)} />
      </tr>

      {/* Add-back rows */}
      {ebitdaAddBacks.map(({ category, label }) => {
        // Skip categories that don't exist in the template
        if (!hasEBITDACategory(category)) return null;
        
        return (
          <tr key={category}>
            <td className="sticky left-0 z-10 bg-gray-50 px-6 py-4 text-sm text-gray-900 border-r border-gray-200">{label}</td>
            <td className="px-6 py-4 text-sm font-semibold" />
            {periods.map(period => {
              const amount = calculateCategoryTotal(
                accounts,
                category,
                period,
                viewType,
                false,
                false
              );
              const netRevenue = calculateNetRevenueTotal(period);
              const percentage = netRevenue !== 0 ? (amount / netRevenue) * 100 : 0;
              return (
                <td key={period} className="px-6 py-4 text-sm text-gray-900 text-right">
                  {percentage.toFixed(1)}%
                </td>
              );
            })}
            {viewType === 'yearly' && (
              <td className="px-6 py-4 text-sm text-gray-900 text-right">
                {(() => {
                  const projectPeriodType = selectedProject?.period_type || 'monthly';
                  const ttmPeriods = getTTMPeriods();
                  const ttmAmount = calculateTTMForPeriodType(
                    accounts,
                    category,
                    allMonths,
                    false,
                    false,
                    projectPeriodType
                  );
                  const ttmNetRevenue = calculateTTMForPeriodType(
                    accounts, 
                    'Sales Revenue', 
                    allMonths, 
                    true, 
                    false, 
                    projectPeriodType
                  );
                  const percentage = ttmNetRevenue !== 0 ? (ttmAmount / ttmNetRevenue) * 100 : 0;
                  return `${percentage.toFixed(1)}%`;
                })()}
              </td>
            )}
          </tr>
        );
      })}

      {/* EBITDA Total */}
      <tr className="bg-gray-300">
        <td className="sticky left-0 z-10 bg-gray-300 px-6 py-4 text-sm font-semibold text-gray-900 border-r border-gray-200">
          EBITDA
        </td>
        <td className="px-6 py-4 text-sm font-semibold" />
        {periods.map(period => {
          const netIncome = calculateNetIncome(period);
          const ebitda = calculateEBITDA(accounts, period, viewType, netIncome);
          const netRevenue = calculateNetRevenueTotal(period);
          const percentage = netRevenue !== 0 ? (ebitda / netRevenue) * 100 : 0;
          
          return (
            <td key={period} className="px-6 py-4 text-sm font-semibold text-gray-900 text-right">
              {percentage.toFixed(1)}%
            </td>
          );
        })}
        {viewType === 'yearly' && ttmNetIncome !== undefined && (
          <td className="px-6 py-4 text-sm font-semibold text-gray-900 text-right">
            {(() => {
              const projectPeriodType = selectedProject?.period_type || 'monthly';
              const ttmPeriods = getTTMPeriods();
              const revenueCats = templateCategories?.filter(cat => cat.type === 'revenue').map(cat => cat.value) || [];
              const contraCats = templateCategories?.filter(cat => cat.type === 'contra-revenue').map(cat => cat.value) || [];
              
              // Calculate TTM Net Revenue using period-aware logic
              const ttmNetRevenue =
                revenueCats.reduce((total, cat) => total + calculateTTMForPeriodType(accounts, cat, allMonths, true, false, projectPeriodType), 0) +
                contraCats.reduce((total, cat) => total + calculateTTMForPeriodType(accounts, cat, allMonths, true, true, projectPeriodType), 0);
              
              // Calculate EBITDA components using period-aware logic, checking if they exist in the template
              const ttmComponents = {
                interestExpense: hasEBITDACategory('Interest Expense (Income)') ? 
                  calculateTTMForPeriodType(accounts, 'Interest Expense (Income)', allMonths, false, false, projectPeriodType) : 0,
                incomeTaxExpense: hasEBITDACategory('Income Tax Expense') ? 
                  calculateTTMForPeriodType(accounts, 'Income Tax Expense', allMonths, false, false, projectPeriodType) : 0,
                depreciation: hasEBITDACategory('Depreciation') ? 
                  calculateTTMForPeriodType(accounts, 'Depreciation', allMonths, false, false, projectPeriodType) : 0,
                amortization: hasEBITDACategory('Amortization') ? 
                  calculateTTMForPeriodType(accounts, 'Amortization', allMonths, false, false, projectPeriodType) : 0
              };
              
              const ttmEBITDA = ttmNetIncome + 
                ttmComponents.interestExpense + 
                ttmComponents.incomeTaxExpense + 
                ttmComponents.depreciation + 
                ttmComponents.amortization;
              
              const ttmEbitdaPercent = ttmNetRevenue !== 0 ? (ttmEBITDA / ttmNetRevenue) * 100 : 0;
              return `${ttmEbitdaPercent.toFixed(1)}%`;
            })()}
          </td>
        )}
      </tr>
    </>
  );
};